import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export interface TabResource {
  type: 'document' | 'extractedData' | 'annotations' | 'validation' | 'work'
  id: string
  meta?: Record<string, any>
}

export interface Tab {
  id: string
  label: string
  type: 'home' | 'validation' | 'data' | 'transactions' | 'settings' | 'file' | 'summary' | 'flag' | 'research' | 'notes' | 'json-extracted' | 'json-annotations'
  closable?: boolean
  iconName?: string
  resource?: TabResource  // Reference to the resource this tab displays
}

interface TabsState {
  openTabs: Tab[]
  activeTabId: string
}

const initialState: TabsState = {
  openTabs: [
    {
      id: 'home',
      label: 'Home',
      type: 'home',
      closable: false,
      iconName: 'Home'
    }
  ],
  activeTabId: 'home'
}

const tabsSlice = createSlice({
  name: 'tabs',
  initialState,
  reducers: {
    openTab(state, action: PayloadAction<Tab>) {
      const existingTabIndex = state.openTabs.findIndex(t => t.id === action.payload.id)
      if (existingTabIndex > -1) {
        // Update existing tab with new data
        state.openTabs[existingTabIndex] = action.payload
        console.log('TabsSlice: Updated existing tab:', action.payload.id, 'resource:', action.payload.resource)
      } else {
        // Add new tab
        state.openTabs.push(action.payload)
        console.log('TabsSlice: Added new tab:', action.payload.id, 'resource:', action.payload.resource)
      }
      state.activeTabId = action.payload.id
    },

    closeTab(state, action: PayloadAction<string>) {
      const tabId = action.payload
      
      // Don't close home tab
      if (tabId === 'home') return
      
      const tabIndex = state.openTabs.findIndex(t => t.id === tabId)
      if (tabIndex > -1) {
        state.openTabs.splice(tabIndex, 1)
        
        // If closing active tab, switch to adjacent tab
        if (state.activeTabId === tabId && state.openTabs.length > 0) {
          const newActiveIndex = Math.min(tabIndex, state.openTabs.length - 1)
          state.activeTabId = state.openTabs[newActiveIndex].id
        }
      }
    },

    setActiveTab(state, action: PayloadAction<string>) {
      const tab = state.openTabs.find(t => t.id === action.payload)
      if (tab) {
        state.activeTabId = action.payload
      }
    },

    updateTabResource(state, action: PayloadAction<{ tabId: string; resource: TabResource }>) {
      const { tabId, resource } = action.payload
      const tab = state.openTabs.find(t => t.id === tabId)
      if (tab) {
        tab.resource = resource
        console.log('TabsSlice: Updated tab resource:', tabId, resource)
      }
    },

    clearTabs(state) {
      state.openTabs = [
        {
          id: 'home',
          label: 'Home',
          type: 'home',
          closable: false,
          iconName: 'Home'
        }
      ]
      state.activeTabId = 'home'
    }
  }
})

export const { 
  openTab, 
  closeTab, 
  setActiveTab, 
  updateTabResource,
  clearTabs
} = tabsSlice.actions

export default tabsSlice.reducer
